import React, { useContext, useCallback } from 'react';

import CountryFlag from 'components/base/CountryFlag/index';
import { TopTerritoriesItem } from './TopTerritories';
import { EntityContext } from 'context/entityContext';
import getShortName from 'helpers/getShortName';
import { IEntity, ITopTerritoriesEntityCountryItem } from './types';
import TooltipWrapper from 'components/base/TooltipWrapper';
import { MAX_COUNTRY_NAME } from 'config';

/**
 * TopCountry displays a single TopCountry list item in the
 * SpinsTopTerritories component.
 * @param {object} props React props
 * @param {object} props.item Country item
 * @param {('country'|'entity')} props.compareMode The current compare mode
 * @param {object} props.entity Entity object like { id, type }
 * @param {number} props.rank This countries' rank
 */
interface IProps {
  item: ITopTerritoriesEntityCountryItem;
  compareMode: string;
  entity: IEntity;
  rank: number;
  fullName?: boolean;
}

export const TopCountry: React.FC<IProps> = (props) => {
  const { item, compareMode, entity, rank, fullName } = props;

  const ctx = useContext(EntityContext);
  const { filters, countryIds, dataColors, entityIds } = ctx;

  const getFontWeight = useCallback(
    (countryId) => {
      if (countryId === filters?.country) return 600;
      if (compareMode === 'country' && countryIds?.includes(countryId)) {
        return 600;
      }

      return 400;
    },
    [compareMode, countryIds, filters]
  );

  const getColor = useCallback(
    (countryId) => {
      if (countryIds && compareMode === 'country') {
        const idx = countryIds.findIndex((el) => countryId === el);
        return dataColors[idx];
      }

      if (compareMode === 'entity') {
        const idx = entityIds.findIndex((el) => entity.id === el);
        return dataColors[idx];
      }

      return;
    },
    [compareMode, countryIds, dataColors, entityIds, entity]
  );

  const name = compareMode
    ? item.name
    : fullName
    ? item.metadata.displayName
    : item.id;
  const shortName = getShortName(name);

  return (
    <TooltipWrapper
      condition={name.length > MAX_COUNTRY_NAME}
      tooltipContent={name}
    >
      <TopTerritoriesItem
        data-rank={rank}
        fontWeight={getFontWeight(item.id)}
        dataColor={getColor(item.id)}
      >
        <>
          {item.metadata && <CountryFlag {...item.metadata} small />}
          {shortName}
        </>
      </TopTerritoriesItem>
    </TooltipWrapper>
  );
};
